import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Repeat, MapPin, Sparkles, AlertTriangle, Check, X } from "lucide-react";
import { toast } from "sonner";
import { shiftSwapApi } from "../../services/schedulingApi";
import { StoreSelect } from "../../components/StoreSelect";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { useLocalization } from "../../hooks/useLocalization";

const STATUS_STYLES = {
  PENDING_REPLACEMENT_RESPONSE: "bg-amber-100 text-amber-700 border-amber-200",
  PENDING_MANAGER_APPROVAL: "bg-blue-100 text-blue-700 border-blue-200",
  APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  REPLACEMENT_DECLINED: "bg-gray-100 text-gray-600 border-gray-200",
  REJECTED: "bg-red-100 text-red-700 border-red-200",
  CANCELLED: "bg-gray-100 text-gray-500 border-gray-200",
  EXPIRED: "bg-gray-100 text-gray-500 border-gray-200",
  REVALIDATION_REQUIRED: "bg-orange-100 text-orange-700 border-orange-200",
};
const FILTERABLE_STATUSES = ["PENDING_MANAGER_APPROVAL", "PENDING_REPLACEMENT_RESPONSE", "REVALIDATION_REQUIRED", "APPROVED", "REJECTED", "REPLACEMENT_DECLINED", "CANCELLED", "EXPIRED"];

export default function ShiftSwapManagementPage() {
  const { t } = useLocalization();
  const [requests, setRequests] = useState([]);
  const [storeFilter, setStoreFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [deciding, setDeciding] = useState(false);

  const load = () => {
    setLoading(true);
    shiftSwapApi.listRequests(storeFilter, statusFilter).then(({ data }) => setRequests(data)).catch(() => toast.error(t("shiftSwap.loadError"))).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [storeFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (id) => {
    setRejectionReason("");
    try {
      const { data } = await shiftSwapApi.getRequest(id);
      setSelected(data);
    } catch {
      toast.error(t("shiftSwap.loadError"));
    }
  };

  const handleDecide = async (approve) => {
    if (!selected) return;
    if (!approve && !rejectionReason.trim()) {
      toast.error(t("shiftSwap.rejectionReasonRequiredError"));
      return;
    }
    setDeciding(true);
    try {
      const { data } = await shiftSwapApi.decide(selected.id, approve, rejectionReason.trim() || undefined);
      if (data.status === "REVALIDATION_REQUIRED") {
        toast.warning(t("shiftSwap.revalidationFailedToast"));
      } else {
        toast.success(approve ? t("shiftSwap.approvedToast") : t("shiftSwap.rejectedToast"));
      }
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("shiftSwap.decideError"));
    } finally {
      setDeciding(false);
    }
  };

  const pendingCount = useMemo(() => requests.filter((r) => r.status === "PENDING_MANAGER_APPROVAL").length, [requests]);

  return (
    <div className="space-y-5" data-testid="shift-swap-management-page">
      <PageHelpBanner pageKey="shift-swaps" title={t("pageHelp.defaultTitle")}>
        <p>{t("pageHelp.shiftSwaps")}</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-gray-900 flex items-center gap-2"><Repeat size={20} /> {t("shiftSwap.managementTitle")}</h2>
          <p className="text-sm text-gray-500">{t("shiftSwap.managementSubtitle")}</p>
        </div>
        {pendingCount > 0 && (
          <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold" data-testid="swap-pending-approval-count">{t("shiftSwap.pendingApprovalCount", { count: pendingCount })}</span>
        )}
      </div>

      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3">
        <div className="w-56"><StoreSelect testId="swap-filter-store" value={storeFilter} onChange={setStoreFilter} allowEmpty emptyLabel={t("common.organizationWideAllStores")} /></div>
        <select data-testid="swap-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm">
          <option value="">{t("shiftSwap.allStatuses")}</option>
          {FILTERABLE_STATUSES.map((s) => <option key={s} value={s}>{t(`shiftSwap.status.${s}`)}</option>)}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <p className="text-sm text-gray-400 text-center py-10">{t("common.loading")}</p>
        ) : requests.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-10" data-testid="shift-swap-queue-empty">{t("shiftSwap.queueEmpty")}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2.5">{t("shiftSwap.colRequester")}</th>
                <th className="text-left px-4 py-2.5">{t("shiftSwap.colReplacement")}</th>
                <th className="text-left px-4 py-2.5">{t("shiftSwap.colShift")}</th>
                <th className="text-left px-4 py-2.5">{t("shiftSwap.colStore")}</th>
                <th className="text-left px-4 py-2.5">{t("shiftSwap.colStatus")}</th>
                <th className="text-left px-4 py-2.5">{t("shiftSwap.colAge")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {requests.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openDetail(r.id)} data-testid={`swap-queue-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{r.requester_name || r.requester_employee_id.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-gray-600">{r.replacement_name || (r.replacement_employee_id ? r.replacement_employee_id.slice(0, 8) : "-")}</td>
                  <td className="px-4 py-3 text-gray-600">{dayjs(r.shift_snapshot.date).format("MMM D")} {r.shift_snapshot.start_time}-{r.shift_snapshot.end_time}
                    {r.shift_snapshot.is_qualifying_shift && <Sparkles size={12} className="inline ml-1 text-amber-500" />}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{r.shift_snapshot.store_location}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${STATUS_STYLES[r.status] || ""}`}>{t(`shiftSwap.status.${r.status}`)}</span></td>
                  <td className="px-4 py-3 text-gray-400">{dayjs(r.created_at).format("MMM D, HH:mm")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="max-w-lg overflow-y-auto" data-testid="shift-swap-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2"><Repeat size={18} /> {t("shiftSwap.detailTitle")}</SheetTitle>
              </SheetHeader>

              <div className="space-y-4 mt-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
                  <p className="text-sm font-bold text-gray-900">{dayjs(selected.shift_snapshot.date).format("dddd, MMM D, YYYY")}</p>
                  <p className="text-sm text-gray-600">{selected.shift_snapshot.start_time} - {selected.shift_snapshot.end_time}{selected.shift_snapshot.crosses_midnight && ` (${t("shiftSwap.crossesMidnight")})`}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin size={12} /> {selected.shift_snapshot.store_location} {selected.shift_snapshot.role ? `· ${selected.shift_snapshot.role}` : ""}</p>
                  {selected.shift_snapshot.is_qualifying_shift && (
                    <p className="text-xs text-amber-700 flex items-center gap-1 mt-1"><Sparkles size={12} /> {t("shiftSwap.qualifyingShiftLabel")}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-500">{t("shiftSwap.colRequester")}</p>
                    <p className="text-sm text-gray-900" data-testid="swap-detail-requester">{selected.requester_name || selected.requester_employee_id}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500">{t("shiftSwap.colReplacement")}</p>
                    <p className="text-sm text-gray-900" data-testid="swap-detail-replacement">{selected.replacement_name || selected.replacement_employee_id || t("shiftSwap.noneYet")}</p>
                  </div>
                </div>

                {selected.reason && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500">{t("shiftSwap.reasonLabel")}</p>
                    <p className="text-sm text-gray-700">{selected.reason}</p>
                  </div>
                )}

                <div>
                  <p className="text-xs font-semibold text-gray-500">{t("shiftSwap.statusHistoryLabel")}</p>
                  <div className="space-y-1 mt-1">
                    {selected.status_history.map((h, i) => (
                      <p key={i} className="text-xs text-gray-500">{dayjs(h.at).format("MMM D, HH:mm")} — {t(`shiftSwap.status.${h.status}`)}{h.note ? `: ${h.note}` : ""}</p>
                    ))}
                  </div>
                </div>

                {selected.eligibility_snapshot?.failure && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2" data-testid="swap-detail-revalidation-failure">
                    <AlertTriangle size={15} className="text-orange-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-orange-700">{selected.eligibility_snapshot.failure}</p>
                  </div>
                )}

                {selected.status === "PENDING_MANAGER_APPROVAL" && (
                  <div className="space-y-2 pt-2 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-500">{t("shiftSwap.rejectionReasonOptionalLabel")}</p>
                    <Textarea data-testid="swap-rejection-reason-input" rows={2} value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder={t("shiftSwap.reasonPlaceholder")} />
                  </div>
                )}
              </div>

              {selected.status === "PENDING_MANAGER_APPROVAL" && (
                <SheetFooter className="mt-4 gap-2">
                  <Button variant="outline" onClick={() => handleDecide(false)} disabled={deciding} data-testid="swap-reject-btn" className="flex items-center gap-1.5">
                    <X size={14} /> {t("shiftSwap.rejectSwap")}
                  </Button>
                  <Button onClick={() => handleDecide(true)} disabled={deciding} data-testid="swap-approve-btn" className="flex items-center gap-1.5">
                    <Check size={14} /> {t("shiftSwap.approveSwap")}
                  </Button>
                </SheetFooter>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
